<template>
  <div class="class-select">
    <div class="card">
      <b-loading :is-full-page="false" :active="isLoading"></b-loading>
      <div class="class-select__controls">
        <div class="is-flex" style="width: 100%; gap: 1rem">
          <b-field label="Year" label-position="on-border">
            <b-select v-model="localSelectedYear" expanded>
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </b-select>
          </b-field>

          <b-field label="Term" label-position="on-border">
            <b-select
              v-model="localSelectedTerm"
              expanded
              :disabled="!localSelectedYear"
            >
              <option
                v-for="term in terms"
                :key="term.value"
                :value="term.value"
              >
                {{ term.label }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div style="width: 100%">
          <b-field label="Class" label-position="on-border">
            <b-select
              v-model="localSelectedClassIndex"
              expanded
              :disabled="!localSelectedYear || !localSelectedTerm"
            >
              <option
                v-if="
                  classList.length === 0 &&
                  !isLoading &&
                  localSelectedYear &&
                  localSelectedTerm
                "
                :value="null"
                disabled
              >
                No classes found
              </option>
              <option
                v-for="(item, index) in classList"
                :key="item.classId"
                :value="index"
              >
                {{ formatClass(item) }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div v-if="classList.length > 1" class="class-select__navigation">
          <b-button
            icon-left="chevron-left"
            :disabled="selectedClassIndex <= 0"
            @click="navigateClass(-1)"
          />
          <span class="class-select__counter">
            {{ (selectedClassIndex === null ? -1 : selectedClassIndex) + 1 }} /
            {{ classList.length }} classes
          </span>
          <b-button
            icon-left="chevron-right"
            :disabled="selectedClassIndex >= classList.length - 1"
            @click="navigateClass(1)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { recentYears } from "vue-chess-shared/utils/date-helper";
import { mapGetters, mapActions } from "vuex";
import SACFrontendService from "@/services/SACFrontendService";

export default {
  name: "ClassSelect",

  components: {},

  props: {
    initialOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      years: [],
      terms: [
        {
          value: 1,
          label: "Term 1",
        },
        {
          value: 2,
          label: "Term 2",
        },
        {
          value: 3,
          label: "Term 3",
        },
        {
          value: 4,
          label: "Term 4",
        },
        {
          value: 5,
          label: "Other",
        },
      ],
      localSelectedYear: new Date().getFullYear(),
      localSelectedTerm: this.getDefaultTerm(),
      localSelectedClassIndex: null,
      classList: [],
    };
  },

  computed: {
    ...mapGetters({
      currentClass: "getCurrentClass",
    }),

    selectedClassDisplay() {
      if (this.currentClass && this.currentClass.classId) {
        return this.formatClass(this.currentClass);
      }
      return null;
    },

    selectedYear() {
      return this.currentClass?.year || this.localSelectedYear;
    },

    selectedTerm() {
      return this.currentClass?.term || this.localSelectedTerm;
    },

    selectedKey() {
      return `${this.localSelectedYear}-${this.localSelectedTerm}`;
    },

    selectedClassIndex() {
      if (this.currentClass && this.classList.length > 0) {
        return this.classList.findIndex(
          (c) =>
            c.classId === this.currentClass.classId &&
            c.classDateId === this.currentClass.classDateId
        );
      }
      return this.localSelectedClassIndex;
    },
  },

  watch: {
    currentClass: {
      immediate: true,
      handler(newCurrentClass) {
        if (newCurrentClass && newCurrentClass.year && newCurrentClass.term) {
          this.localSelectedYear = newCurrentClass.year;
          this.localSelectedTerm = newCurrentClass.term;
        }
      },
    },

    selectedKey: {
      immediate: true,
      handler() {
        if (this.localSelectedYear && this.localSelectedTerm) {
          this.loadClassList(this.localSelectedYear, this.localSelectedTerm);
        }
      },
    },

    localSelectedClassIndex(newValue, oldValue) {
      if (
        newValue !== null &&
        newValue !== oldValue &&
        this.classList[newValue]
      ) {
        this.selectClass(newValue);
      }
    },
  },

  methods: {
    ...mapActions({
      updateCurrentClass: "updateCurrentClass",
      updateClassList: "updateClassList",
    }),

    getDefaultTerm() {
      const month = new Date().getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12

      // Map months to 5 terms (roughly 2.4 months per term)
      // Term 1: Jan-Feb (months 1-2)
      // Term 2: Mar-Apr (months 3-4)
      // Term 3: May-Jul (months 5-7)
      // Term 4: Aug-Oct (months 8-10)
      // Term 5: Nov-Dec (months 11-12)
      if (month <= 2) return 1;
      if (month <= 4) return 2;
      if (month <= 7) return 3;
      if (month <= 10) return 4;
      return 5;
    },

    async loadClassList(year, term) {
      if (!year || !term) {
        return;
      }

      this.isLoading = true;
      this.classList = [];

      try {
        const classList = await SACFrontendService.getClassList(year, term);
        if (!classList.length) {
          this.$buefy.toast.open({
            message:
              "OOPS. There is no class assigned to you in chosen year and term.",
            type: "is-warning",
          });
        }

        // deduplicate class list
        const map = new Map();
        classList.forEach((item) => {
          const key = `${item["schoolName"]}-${item["groupName"]}`;
          if (!map.has(key)) {
            map.set(key, item);
          }
        });
        this.classList = Array.from(map.values());
        this.updateClassList(this.classList);
        if (this.classList.length) {
          this.localSelectedClassIndex = this.currentClass
            ? Math.max(
                0,
                this.classList.findIndex(
                  (c) => c.classId === this.currentClass.classId
                )
              )
            : 0;
        } else {
          this.localSelectedClassIndex = null; // No classes available
        }
      } catch (err) {
        console.error(err);
        SACFrontendService.logout();
        this.$router.replace({ name: "TeacherLogin" });
      } finally {
        this.isLoading = false;
      }
    },

    async selectClass(selectedIndex) {
      if (!this.localSelectedYear || !this.localSelectedTerm) {
        return;
      }

      const classInfo = this.classList[selectedIndex];
      if (!classInfo) {
        return;
      }

      this.updateCurrentClass({
        year: this.localSelectedYear,
        term: this.localSelectedTerm,
        ...classInfo,
      });

      // Emit event when class is selected
      this.$emit("class-selected", classInfo);
    },

    formatClass(classData) {
      // Handle both currentClass format and classList item format
      return `${classData.schoolName}${
        classData.groupName ? ` | ${classData.groupName}` : ""
      }`;
    },

    navigateClass(delta) {
      const currentIndex = this.selectedClassIndex;
      const newIndex = currentIndex + delta;
      if (newIndex >= 0 && newIndex < this.classList.length) {
        this.localSelectedClassIndex = newIndex;
      }
    },
  },

  created() {
    this.years = recentYears(10);
  },
};
</script>

<style scoped>
.class-select {
  position: relative;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  min-width: 300px;
}

.class-select__header {
  background-color: darkslateblue;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.class-select__header-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.class-select__controls {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 0.5rem;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  background-color: #fff;
}

.class-select__navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.custom-controls {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
}

.card {
  margin-bottom: 0;
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.2),
    0 0 0 1px rgba(10, 10, 10, 0.02);
}
</style>
